/* -------------------------------------------------------------------------
 * Sayuri PDP gallery — click-to-swap + active thumb state
 * -------------------------------------------------------------------------
 * Vanilla JS, no dependencies. Finds the gallery container
 * ([data-sayuri-gallery]) on a PDP, binds click handlers on each
 * .sayuri-pdp__thumb, and swaps the main image's src when a thumb
 * is selected. Also toggles the active class + aria-selected for
 * styling + a11y.
 *
 * The first thumb starts active (both in Liquid-rendered markup and
 * this JS's initial scan). Thumbs carry:
 *   data-full-src   — large image URL to swap into main
 *   data-media-type — "image", "video", "external_video", "model"
 *                     (used for future video-embed phase; for now
 *                      we just swap preview_image URLs regardless)
 *
 * Keyboard: buttons handle Enter/Space natively. Arrow keys are a
 * future nice-to-have; not wired here.
 *
 * The JS is idempotent — re-running init() against already-mounted
 * galleries is a no-op (each thumb's click handler is added once).
 * ----------------------------------------------------------------------- */
(function () {
  'use strict';

  function init() {
    var galleries = document.querySelectorAll('[data-sayuri-gallery]:not([data-sayuri-gallery-mounted])');
    for (var i = 0; i < galleries.length; i++) mount(galleries[i]);
  }

  function mount(gallery) {
    gallery.setAttribute('data-sayuri-gallery-mounted', '1');

    var main = gallery.querySelector('.sayuri-pdp__gallery-main');
    var thumbs = gallery.querySelectorAll('.sayuri-pdp__thumb');
    if (!main || thumbs.length === 0) return;

    for (var i = 0; i < thumbs.length; i++) {
      (function (thumb) {
        thumb.addEventListener('click', function () { select(gallery, thumb); });
      })(thumbs[i]);
    }

    // Desktop-only: cap the vertical thumb rail to the main image's height and
    // scroll the overflow. The Liquid now renders ALL media as thumbs (so
    // per-size photos aren't truncated), which can exceed the main image's
    // height; without this the rail would run past the bottom of the image.
    // On mobile the rail is a horizontal wrapping row, so we leave it alone.
    syncThumbHeight(gallery);
    var mainWrap = gallery.querySelector('.sayuri-pdp__gallery-main-wrap');
    if (mainWrap && typeof ResizeObserver === 'function') {
      var ro = new ResizeObserver(function () { syncThumbHeight(gallery); });
      ro.observe(mainWrap);
    }
    window.addEventListener('resize', function () { syncThumbHeight(gallery); });
  }

  function syncThumbHeight(gallery) {
    var rail = gallery.querySelector('.sayuri-pdp__gallery-thumbs');
    var mainWrap = gallery.querySelector('.sayuri-pdp__gallery-main-wrap');
    if (!rail || !mainWrap) return;
    var isDesktop = window.matchMedia('(min-width: 961px)').matches;
    if (isDesktop) {
      var h = mainWrap.offsetHeight;
      if (h > 0) {
        rail.style.maxHeight = h + 'px';
        rail.style.overflowY = 'auto';
      }
    } else {
      // Horizontal wrapping row on mobile — clear any desktop cap.
      rail.style.maxHeight = '';
      rail.style.overflowY = '';
    }
  }

  // Public: swap the gallery's main image to a specific media, identified by
  // media id (preferred) or by its full-size src (fallback). Used by the PDP
  // size picker so selecting a Size shows that size's photo. Matching a thumb
  // reuses select() (main swap + active-thumb state); if no thumb matches
  // (e.g. that media isn't a thumb), the main image is swapped directly.
  function showMedia(mediaId, src, galleryEl) {
    var gallery = galleryEl || document.querySelector('[data-sayuri-gallery]');
    if (!gallery) return false;
    var thumbs = gallery.querySelectorAll('.sayuri-pdp__thumb');
    var target = null;
    var i;
    if (mediaId != null && mediaId !== '') {
      var mid = String(mediaId);
      for (i = 0; i < thumbs.length; i++) {
        if (thumbs[i].getAttribute('data-media-id') === mid) { target = thumbs[i]; break; }
      }
    }
    if (!target && src) {
      for (i = 0; i < thumbs.length; i++) {
        if (thumbs[i].getAttribute('data-full-src') === src) { target = thumbs[i]; break; }
      }
    }
    if (target) { select(gallery, target); return true; }

    // No matching thumb — swap the main image directly and clear active state.
    if (src) {
      var main = gallery.querySelector('.sayuri-pdp__gallery-main');
      if (main && main.tagName === 'IMG') {
        main.src = src;
        main.srcset = buildSrcset(src);
        var allThumbs = gallery.querySelectorAll('.sayuri-pdp__thumb');
        for (i = 0; i < allThumbs.length; i++) {
          allThumbs[i].classList.remove('sayuri-pdp__thumb--active');
          allThumbs[i].setAttribute('aria-selected', 'false');
        }
      }
      return true;
    }
    return false;
  }

  function select(gallery, thumb) {
    var main = gallery.querySelector('.sayuri-pdp__gallery-main');
    if (!main) return;

    var fullSrc = thumb.getAttribute('data-full-src');
    if (fullSrc && main.tagName === 'IMG') {
      // Replace both src + srcset so responsive picking works.
      // We build a srcset from Shopify's image_url width variants
      // by swapping the ?width= param. The rendered image_tag in
      // Liquid emits srcset like "url?width=400 400w, url?width=600 600w, ...";
      // we regenerate that pattern off the fullSrc base URL.
      main.src = fullSrc;
      main.srcset = buildSrcset(fullSrc);
      main.alt = thumb.getAttribute('aria-label') || main.alt;
    }

    // Toggle active state across thumbs.
    var allThumbs = gallery.querySelectorAll('.sayuri-pdp__thumb');
    for (var i = 0; i < allThumbs.length; i++) {
      var t = allThumbs[i];
      var isActive = t === thumb;
      t.classList.toggle('sayuri-pdp__thumb--active', isActive);
      t.setAttribute('aria-selected', isActive ? 'true' : 'false');
    }
  }

  // Shopify CDN URLs accept a `width=` query parameter that returns
  // a resized image. Build a srcset spanning the same widths the
  // original image_tag used so the browser picks an appropriate size
  // for the current viewport.
  function buildSrcset(baseSrc) {
    var widths = [400, 600, 800, 1200];
    try {
      var url = new URL(baseSrc, window.location.origin);
      return widths.map(function (w) {
        url.searchParams.set('width', String(w));
        return url.toString() + ' ' + w + 'w';
      }).join(', ');
    } catch (e) {
      // If URL parsing fails (e.g. non-standard src), fall back to a
      // single-width src and let the browser do its default.
      return baseSrc;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.SayuriGallery = { init: init, mount: mount, showMedia: showMedia };
})();
